import { FileSearchOutlined, FileExcelOutlined } from '@ant-design/icons'
import { Button, message, Switch, Tabs, Row, Col, Table } from 'antd'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import queryString from 'query-string'
import { createUseStyles } from 'react-jss'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAsync } from '../../../hooks'
import useExportExcel from '../../../hooks/useExportExcel'
import { formatDOB } from '../../../lib/datetime'
import { PAGE_SIZE_OPTIONS } from '../../../lib/constants'
import {
  markBillingOrderAsBilled,
  useBillingOrders,
  useBillingOrdersPagination,
  useFetchBillingOrders,
  useFetchCPTCodes,
  useCPTCodes,
} from '../../../redux/slices/in-office-billing-orders'
import DownloadLink from '../../Common/DownloadLink'
import { toPath } from '../../../routes'
import { ListError } from '../../Common'
import PatientEntry from '../../Common/PatientEntry'

const { TabPane } = Tabs

const useStyles = createUseStyles({
  inactiveRow: {
    '& td': {
      color: '#999 !important',
      fontStyle: 'italic',
    },
    opacity: 0.6,
  },
})

const COLUMNS = [
  {
    title: 'Patient',
    dataIndex: ['patient', 'lastName'],
    render(_, row) {
      return <PatientEntry patient={row.patient} />
    },
  },
  {
    title: 'MRN',
    key: 'MRN',
    dataIndex: ['patient', 'practiceMrn'],
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: ['patient', 'birthDate'],
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Billing Date',
    dataIndex: 'billingDate',
    key: 'billingDate',
    render: formatDOB,
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, billingOrder) {
      if (!billingOrder.preAuthorization) return null

      const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
        billingOrder.preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>{insuranceAuthorizationNumber}</span>
        </DownloadLink>
      )
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render(text, billingOrder) {
      return (
        <Link
          to={toPath.billing.officeBillingOrders.officeBillingOrder({
            id: billingOrder.id,
          })}
        >
          <Button type="link" icon={<FileSearchOutlined />}>
            Comments / More
          </Button>
        </Link>
      )
    },
  },
]

const CPT_CODE_COLUMNS = [
  {
    title: 'CPT Codes',
    dataIndex: 'cptCode',
    key: 'cptCode',
    width: '80%',
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
  },
]

export default function BillingOrders({
  checkDate,
  onPaginationChange,
  page = 1,
  perPage = 50,
  practiceId,
  query = '',
  showInactive,
}) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const billingOrders = useBillingOrders(checkDate, query)
  const { total } = useBillingOrdersPagination(checkDate, query)
  const cptCodes = useCPTCodes()
  const {
    pending: cptPending,
    error: cptError,
    clearError: clearCPTError,
  } = useAsync(useFetchCPTCodes(practiceId))
  const { pending, error, clearError } = useAsync(
    useFetchBillingOrders(practiceId, checkDate, page, query, perPage, showInactive),
  )

  const currentCPTCodes = useMemo(() => {
    const current = cptCodes.find(entry => entry.practiceId === practiceId)

    if (current && current.data) {
      return current.data
    }
    return []
  }, [cptCodes, practiceId])

  const handleBilledChange = useCallback(
    async (id, billed) => {
      try {
        await dispatch(markBillingOrderAsBilled(id, billed))
        message.success('Billing Order has been updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.billed',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const copy = [...COLUMNS]
    copy.splice(copy.length - 1, 0, {
      title: 'Billed',
      key: 'billed',
      render: (text, record) => (
        <Switch
          checked={record.billed}
          checkedChildren="Billed"
          unCheckedChildren="Not&nbsp;Billed"
          onChange={billed => handleBilledChange(record.id, billed)}
        />
      ),
    })
    return copy
  }, [handleBilledChange])

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: `/practices/${practiceId}/in-office-billing-orders.xlsx`,
          query: {
            date: checkDate,
            query,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [practiceId, checkDate, query],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  const rowClassName = useCallback(
    record =>
      record.preAuthorization?.active === false
        ? classes.inactiveRow
        : undefined,
    [classes.inactiveRow],
  )

  return (
    <Tabs defaultActiveKey="1" style={{ marginBottom: 16 }}>
      <TabPane tab="Billing Orders" key="1">
        {error && (
          <ListError description={error.message} onClose={clearError} />
        )}
        <Table
          size="middle"
          columns={columns}
          dataSource={billingOrders}
          rowKey="id"
          loading={pending}
          rowClassName={rowClassName}
          pagination={{
            current: page,
            pageSize: Number(perPage),
            onChange: onPaginationChange,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
        {Boolean(billingOrders.length) && (
          <div>
            <Button
              loading={exportingExcel}
              onClick={exportExcel}
              icon={<FileExcelOutlined />}
            >
              Export this list to Excel
            </Button>
          </div>
        )}
      </TabPane>
      <TabPane tab="CPT Codes Summary" key="2">
        {cptError && (
          <ListError description={cptError.message} onClose={clearCPTError} />
        )}
        <Row>
          <Col span={12}>
            <Table
              size="middle"
              columns={CPT_CODE_COLUMNS}
              dataSource={currentCPTCodes}
              rowKey="id"
              loading={cptPending}
            />
          </Col>
        </Row>
      </TabPane>
    </Tabs>
  )
}

BillingOrders.propTypes = {
  checkDate: PropTypes.string.isRequired,
  practiceId: PropTypes.string.isRequired,
  onPaginationChange: PropTypes.func.isRequired,
  page: PropTypes.number,
  perPage: PropTypes.number,
  query: PropTypes.string,
  showInactive: PropTypes.bool,
}
